import type { DuaPartType } from '@components/DuaPage/DuaPageComponent'

const RESPONSE_EVENT = '__hayirhahNativeDuaResponse'

type NativeBridgeWindow = Window & {
  ReactNativeWebView?: { postMessage?: (_message: string) => void }
  __HAYIRHAH_NATIVE__?: {
    requestDuaPage?: (_slug: string, _page: number, _requestId: string) => void
  }
}

type NativeDuaResponse = {
  requestId: string
  slug: string
  page: number
  view: 'sections' | 'grouped' | null
  data: unknown
}

export type NativeDuaPageData =
  | { view: 'sections'; data: DuaPartType[] }
  | { view: 'grouped'; data: Record<string, DuaPartType[]> }

export type NativeDuaPage = NativeDuaPageData & {
  slug: string
  page: number
}

/** Checks the bridge request boundary shared by normal duas and Cevşen. */
const isValidDuaRequest = (slug: string, page: number): boolean =>
  /^[a-z0-9-]{1,100}$/.test(slug) && Number.isInteger(page) && page >= 1 && page <= 101

/** Returns the browser window when this page runs inside the native WebView. */
function getNativeWindow(): NativeBridgeWindow | null {
  return typeof window === 'undefined' ? null : (window as NativeBridgeWindow)
}

/** Reports whether the page can send requests to the React Native wrapper. */
export function hasNativeDuaBridge(): boolean {
  const nativeWindow = getNativeWindow()
  return Boolean(
    nativeWindow?.__HAYIRHAH_NATIVE__?.requestDuaPage ||
    nativeWindow?.ReactNativeWebView?.postMessage,
  )
}

/** Requests one bundled dua page from the native content package. */
export function getNativeDuaPage(
  slug: string,
  page: number,
  timeoutMs = 2500,
): Promise<NativeDuaPage | null> {
  const nativeWindow = getNativeWindow()
  if (
    !nativeWindow ||
    !hasNativeDuaBridge() ||
    !isValidDuaRequest(slug, page) ||
    timeoutMs <= 0
  ) {
    return Promise.resolve(null)
  }

  const requestId = `dua-${slug}-${page}-${Date.now()}-${Math.random().toString(36).slice(2)}`

  return new Promise((resolve) => {
    let settled = false
    const finish = (value: NativeDuaPage | null) => {
      if (settled) return
      settled = true
      window.removeEventListener(RESPONSE_EVENT, handleResponse as EventListener)
      window.clearTimeout(timeoutId)
      resolve(value)
    }

    const handleResponse = (event: Event) => {
      const response = (event as CustomEvent<NativeDuaResponse>).detail
      if (
        !response ||
        response.requestId !== requestId ||
        response.slug !== slug ||
        response.page !== page
      ) {
        return
      }

      const data = parseNativeDuaPageData(response)
      finish(data ? { slug, page, ...data } : null)
    }

    const timeoutId = window.setTimeout(() => finish(null), timeoutMs)
    window.addEventListener(RESPONSE_EVENT, handleResponse as EventListener)

    try {
      if (nativeWindow.__HAYIRHAH_NATIVE__?.requestDuaPage) {
        nativeWindow.__HAYIRHAH_NATIVE__.requestDuaPage(slug, page, requestId)
        return
      }

      nativeWindow.ReactNativeWebView?.postMessage?.(
        JSON.stringify({ type: 'getDuaPage', slug, page, requestId }),
      )
    } catch {
      finish(null)
    }
  })
}

/** Validates the native response and preserves its reader-specific data shape. */
function parseNativeDuaPageData(response: NativeDuaResponse): NativeDuaPageData | null {
  if (response.view === 'sections' && Array.isArray(response.data)) {
    return response.data.every(isDuaPart)
      ? { view: 'sections', data: response.data }
      : null
  }

  if (response.view !== 'grouped' || !isRecord(response.data)) return null

  const groupedData: Record<string, DuaPartType[]> = {}
  for (const [section, parts] of Object.entries(response.data)) {
    if (!Array.isArray(parts) || !parts.every(isDuaPart)) return null
    groupedData[section] = parts
  }

  return { view: 'grouped', data: groupedData }
}

/** Checks the minimal fields required for the dua reader to render one part. */
function isDuaPart(value: unknown): value is DuaPartType {
  if (!isRecord(value)) return false
  return typeof value.arab === 'string' && typeof value.meal === 'string'
}

/** Narrows an unknown bridge payload to a plain object. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
